import React, { forwardRef, useCallback, useEffect, useId, useRef, useState } from "react";
import clsx from "clsx";
import { IconWarning } from "icons";
import { useAnimatedBorderContextSafe } from "components/AnimatedGradientBorder/AnimatedGradientBorderContext";

interface ITextarea extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  icon?: React.ReactNode;
  className?: string;
  containerClassName?: string;
  children?: React.ReactNode;
  error?: string;
  PlusAIcomponent?: React.FunctionComponent;
  isCounterActive?: boolean;
  handleCharacterExceed?: (exceeded: boolean) => void;
}

export const TEXTAREA_CHARACTER_LIMIT = 2500;

const Textarea = forwardRef<HTMLTextAreaElement, ITextarea>((props, ref) => {
  const { className, containerClassName, icon, error, PlusAIcomponent, isCounterActive, handleCharacterExceed, value, onChange, ...etc } = props;

  const [characterCount, setCharacterCount] = useState(0);
  const idNumber = useId();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const internalRef = (ref as React.MutableRefObject<HTMLTextAreaElement>) || textareaRef;

  const borderContext = useAnimatedBorderContextSafe();
  const setIsError = isCounterActive && borderContext ? borderContext.setIsError : undefined;

  const updateCharacterCount = useCallback(
    (text: string) => {
      if (!isCounterActive) return;

      const length = text.length;
      setCharacterCount(length);
      const isExceeded = length > TEXTAREA_CHARACTER_LIMIT;
      handleCharacterExceed?.(isExceeded);
      setIsError?.(isExceeded);
    },
    [isCounterActive, handleCharacterExceed, setIsError]
  );

  useEffect(() => {
    if (!isCounterActive) return;

    const currentValue = value || (internalRef.current?.value ?? "");
    updateCharacterCount(String(currentValue));
  }, [value, isCounterActive, updateCharacterCount, internalRef]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      updateCharacterCount(e.target.value);
      onChange?.(e);
    },
    [onChange, updateCharacterCount]
  );

  return (
    <>
      <div className={clsx("input-container flex flex-col gap-2 p-4", "border border-black-300 px-2.5 py-3 rounded-[5px]", containerClassName)}>
        <textarea ref={internalRef} id={`Textarea_${idNumber}`} className={clsx("input overflow-y-scroll resize-y", className)} value={value} onChange={handleChange} {...etc} />
        {PlusAIcomponent && (
          <div className="flex flex-1 justify-between w-full h-full">
            <PlusAIcomponent />
            {isCounterActive && (
              <span className={clsx("text-bodySm", characterCount > TEXTAREA_CHARACTER_LIMIT ? "text-orange" : "text-grey")}>
                {characterCount}/{TEXTAREA_CHARACTER_LIMIT}
              </span>
            )}
          </div>
        )}
        {icon}
      </div>
      {error && (
        <span className="flex text-orange text-h6 items-center gap-[5px]">
          <IconWarning className="h-[17px] w-[17px]" />
          {error}
        </span>
      )}
    </>
  );
});

export default Textarea;
Textarea.displayName = "Textarea";
